import { useEffect, useImperativeHandle, forwardRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import AsyncSelect from "react-select/async";
import type { GroupBase } from "react-select";
import { useForm } from "../useForm";
import InputTextOnly from "../../controls/InputTextOnly";
import SelectMenu from "../../controls/SelectMenu";
import OrderProductsTable from "./OrderProductsTable";
import DeleteConfirmationForm from "../DeleteConfirmationForm";
import DatePickerInput from "../../controls/DatePickerInput";
import AddProductToOrderForm from "./modals/AddProductToOrderForm";
import OrderNotes from "./modals/OrderNotes";
import { convertToDefEventPara, orderStatusOptions } from "../../../shared/utility";
import { normalizeOrderItemUpdatePayload } from "../../../shared/orderItemPayload";
import type { ApiMessage, ApiResult, SelectOption } from "../../../types/api";
import type {
  AddOrderProductPayload, OrderItemRecord, OrderRecord, OrderStatus, ProductDropdownOption,
  UpdateOrderItemPayload, UpdateOrderPayload,
} from "../../../types/orders";
import type { EmptyModalState } from "../../modals/EmptyModal";

interface OrderTotals { subtotal_dollar_amount: number; total_dollar_amount: number; dollar_tax_amount: number }
interface OrderFormValues extends OrderTotals {
  id: number;
  customer: SelectOption | null;
  customer_id: number;
  customer_group_id: number | null;
  created_by: SelectOption | null;
  shipped_by: SelectOption | null;
  po_number: string;
  order_date: string | Date | null;
  expected_date: string | Date | null;
  status: SelectOption<OrderStatus> | null;
  notes: string;
  order_items: OrderItemRecord[];
}
interface OrderDetailsFormProps {
  customers: { customers: SelectOption[] };
  getMyProducts: (search: string) => Promise<ProductDropdownOption[]>;
  employeeOptions: SelectOption[];
  isEdit: boolean;
  order: OrderRecord;
  setEmptyModelOpen: Dispatch<SetStateAction<EmptyModalState>>;
  updateOrderApiCall: (payload: UpdateOrderPayload) => Promise<ApiResult<{ message: string; order: OrderRecord }>>;
  onUpdateOrderItem: (payload: UpdateOrderItemPayload) => Promise<ApiResult<ApiMessage & { order: OrderTotals }>>;
  deleteOrderItemApiCall: (id: number) => Promise<ApiResult<ApiMessage & { order_item: OrderItemRecord }>>;
  addProductToOrderApiCall: (orderId: number, payload: AddOrderProductPayload) => Promise<ApiResult<OrderItemRecord>>;
}
export interface OrderDetailsFormHandle { submit: () => void }

const initialFValues: OrderFormValues = {
  id: 0, customer: null, customer_id: 0, customer_group_id: null, created_by: null,
  shipped_by: null, po_number: "", order_date: null, expected_date: null, status: null,
  notes: "", subtotal_dollar_amount: 0, total_dollar_amount: 0, dollar_tax_amount: 0,
  order_items: [],
};

const OrderDetailsForm = forwardRef<OrderDetailsFormHandle, OrderDetailsFormProps>(
function OrderDetailsForm(props, ref) {
  const {
    customers, getMyProducts, employeeOptions, isEdit, order, setEmptyModelOpen,
    updateOrderApiCall, onUpdateOrderItem, deleteOrderItemApiCall, addProductToOrderApiCall,
  } = props;
  const [asyncSelectValue, setAsyncSelectValue] = useState<SelectOption>({ value: -1, label: "" });

  const validate = (fieldValues: Partial<OrderFormValues>) => {
    const nextErrors: Partial<Record<keyof OrderFormValues, string | null>> = {};
    nextErrors.customer = fieldValues.customer ? null : "Please fill out field.";
    setErrors(nextErrors);
    return Object.values(nextErrors).every((error) => error === null);
  };
  const { values, setValues, errors, setErrors, handleInputChange } =
    useForm(initialFValues, false, validate);

  useEffect(() => {
    const status = orderStatusOptions.find((option) => option.value === order.status) as SelectOption<OrderStatus> | undefined;
    setValues({
      ...initialFValues,
      ...order,
      po_number: order.po_number && String(order.po_number).toLowerCase() !== "null" ? order.po_number : "",
      notes: order.notes ?? "",
      status: status ?? null,
    });
  }, [order, setValues]);

  const updateItemLocally = (newData: OrderItemRecord, index: number, totals: OrderTotals) => {
    setValues((current) => {
      const orderItems = [...current.order_items];
      orderItems[index] = newData;
      return { ...current, order_items: orderItems, ...totals };
    });
  };

  const onItemEditComplete = async (newData: OrderItemRecord, index: number) => {
    const result = await onUpdateOrderItem(normalizeOrderItemUpdatePayload(newData));
    if (result.success) updateItemLocally(newData, index, result.data.order);
    return result;
  };

  const addProductToOrder = async (
    selected: ProductDropdownOption & { id: number; quantity: number | string | null },
  ) => {
    setAsyncSelectValue({ value: -1, label: "" });
    const payload: AddOrderProductPayload = {
      product_id: Number(selected.product_id ?? selected.value),
      quantity: Number(selected.quantity),
      price: Number(selected.price ?? selected.unit_price ?? 0),
      discount: typeof selected.discount === "number" ? selected.discount : 0,
      tax_rate: typeof selected.tax_rate === "number" ? selected.tax_rate : 0,
    };
    const result = await addProductToOrderApiCall(values.id, payload);
    if (result.success) {
      const item = result.data;
      setValues((current) => ({
        ...current,
        order_items: [item, ...current.order_items],
        subtotal_dollar_amount: Number(current.subtotal_dollar_amount) + Number(item.sub_dollar_amount),
        total_dollar_amount: Number(current.total_dollar_amount) + Number(item.total_dollar_amount),
        dollar_tax_amount: Number(current.dollar_tax_amount) +
          (Number(item.total_dollar_amount) - Number(item.sub_dollar_amount)),
      }));
    }
    setEmptyModelOpen({ childComponent: null, open: false });
  };

  const updateOrder = () => {
    if (!validate(values) || !values.customer || !values.status) return;
    void updateOrderApiCall({
      customer_id: Number(values.customer.value),
      created_by: values.created_by, shipped_by: values.shipped_by, order_date: values.order_date,
      expected_date: values.expected_date, po_number: values.po_number, status: values.status,
      notes: values.notes,
    });
  };
  useImperativeHandle(ref, () => ({ submit: updateOrder }));

  const updateDeleteOrderState = (itemId: number) => {
    setValues((current) => {
      const items = current.order_items.filter((item) => item.id !== itemId);
      const subtotal = items.reduce((sum, item) => sum + Number(item.sub_dollar_amount), 0);
      const total = items.reduce((sum, item) => sum + Number(item.total_dollar_amount), 0);
      return { ...current, order_items: items, subtotal_dollar_amount: subtotal,
        total_dollar_amount: total, dollar_tax_amount: total - subtotal };
    });
  };
  const askDeleteOrderItem = (product: OrderItemRecord) => {
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Delete"
          confirmAction={async () => {
            const result = await deleteOrderItemApiCall(product.id);
            if (result.success) updateDeleteOrderState(product.id);
          }}
          message={`Are you sure you want to delete ${product.product_name}?`}
          setOpen={setEmptyModelOpen}
        />
      ),
      open: true,
    });
  };

  const loadOptions = async (input: string): Promise<ProductDropdownOption[]> =>
    input.length > 2 ? getMyProducts(input) : [];

  return (
    <div className="space-y-4">
      <div className="card-custom p-3 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-5">
        <SelectMenu parentClass="md:col-span-2" handleInputChange={handleInputChange} name="customer"
          options={customers.customers} defaultValue={isEdit ? order.customer : null}
          title="Customer" error={errors.customer ?? undefined} />
        <InputTextOnly label="Customer PO #" name="po_number" onChange={handleInputChange}
          value={values.po_number} labelOn type="text" error={errors.po_number ?? undefined}
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm" />
        <SelectMenu handleInputChange={handleInputChange} name="created_by" options={employeeOptions}
          defaultValue={isEdit ? order.created_by : null} title="Salesperson"
          error={errors.created_by ?? undefined} />
        <SelectMenu handleInputChange={handleInputChange} name="shipped_by" options={employeeOptions}
          defaultValue={isEdit ? order.shipped_by : null} title="Shipped by"
          error={errors.shipped_by ?? undefined} />
        <DatePickerInput label="Order Date" name="order_date" onChange={handleInputChange}
          value={values.order_date} labelOn minDate={new Date()} dateFormat="LLL dd, YYY" />
        <DatePickerInput label="Expected Date" name="expected_date" onChange={handleInputChange}
          value={values.expected_date} labelOn minDate={new Date()} dateFormat="LLL dd, YYY"
          showButtonBar />
        <SelectMenu handleInputChange={handleInputChange} name="status"
          options={orderStatusOptions.filter((option) => option.value !== -1)}
          value={isEdit ? values.status : null} title="Status" error={errors.status ?? undefined} />
      </div>
      <div className="card-custom p-3">
        <div className="flex gap-2">
          <p className="text-md text-gray-700 font-semibold">Notes</p>
          <button type="button" onClick={() => setEmptyModelOpen({
            childComponent: <OrderNotes orderNote={values.notes}
              confirmAction={(note) => handleInputChange(convertToDefEventPara("notes", note))}
              setOpen={(state) => setEmptyModelOpen({ childComponent: null, ...state })} />,
            open: true,
          })} className="text-sm font-semibold main-purple">Edit</button>
        </div>
        <p className="text-sm text-gray-700 font-normal">{values.notes || <br />}</p>
      </div>
      <div className="card-custom p-3 py-5">
        <AsyncSelect<ProductDropdownOption, false, GroupBase<ProductDropdownOption>>
          cacheOptions loadOptions={loadOptions} options={[]} className="max-w-3xl"
          onChange={(selected: ProductDropdownOption | null) => {
            if (!selected) return;
            setEmptyModelOpen({
              childComponent: <AddProductToOrderForm product={selected}
                confirmAction={addProductToOrder} isEdit={false}
                setOpen={() => {
                  setEmptyModelOpen({ childComponent: null, open: false });
                  setAsyncSelectValue({ value: -1, label: "" });
                }} />,
              open: true,
            });
          }}
          value={asyncSelectValue as ProductDropdownOption}
        />
        <OrderProductsTable loading={false} values={values}
          askDeleteOrderItem={askDeleteOrderItem} onItemEditComplete={onItemEditComplete} />
      </div>
    </div>
  );
});

export default OrderDetailsForm;
